import React, { useState, useRef, useEffect } from 'react';
import { McIcon } from '@maersk-global/mds-react-wrapper';

interface ResultFilterBarProps {
  columns: { id: string; label?: string }[];
  filters: Record<string, string>;
  onFilterChange: (filters: Record<string, string>) => void;
}

export const ResultFilterBar: React.FC<ResultFilterBarProps> = ({ columns, filters, onFilterChange }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const activeFilters = Object.entries(filters).filter(([, v]) => v && v.trim());

  // Close popover on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        setPickerOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [pickerOpen]);

  const addFilter = (colId: string) => {
    if (!filters[colId] && filters[colId] !== '') {
      onFilterChange({ ...filters, [colId]: '' });
    }
    setPickerOpen(false);
    setSearch('');
    // Focus the new input after render
    setTimeout(() => {
      const el = document.getElementById(`result-filter-${colId}`);
      if (el) {
        const input = el.querySelector('input') || el;
        (input as HTMLElement).focus();
      }
    }, 50);
  };

  const removeFilter = (colId: string) => {
    const next = { ...filters };
    delete next[colId];
    onFilterChange(next);
  };

  const updateFilter = (colId: string, value: string) => {
    onFilterChange({ ...filters, [colId]: value });
  };

  const clearAll = () => onFilterChange({});

  // Columns that don't already have an active filter entry
  const availableColumns = columns.filter(
    (col) => !(col.id in filters)
  );
  const filteredAvailable = search
    ? availableColumns.filter((col) =>
        (col.label || col.id).toLowerCase().includes(search.toLowerCase())
      )
    : availableColumns;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 }}>
      {/* Active filter chips with inline input */}
      {Object.entries(filters).map(([colId, value]) => {
        const col = columns.find((c) => c.id === colId);
        const label = col?.label || colId;
        return (
          <div
            key={colId}
            id={`result-filter-${colId}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0,
              border: '1px solid var(--mds-color-neutral-weak-decoration, #d1d5db)',
              borderRadius: 20,
              backgroundColor: value ? 'var(--mds-color-primary-weakest-background, #e8f4fd)' : 'var(--mds-color-neutral-weakest-background, #f9fafb)',
              paddingLeft: 10,
              height: 30,
              transition: 'background-color 0.15s ease, border-color 0.15s ease',
              ...(value ? { borderColor: 'var(--mds-color-primary-main-background, #0077c5)' } : {}),
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--mds-color-neutral-strong-text, #374151)',
                whiteSpace: 'nowrap',
                userSelect: 'none',
              }}
            >
              {label}
            </span>
            <input
              type="text"
              value={value}
              onChange={(e) => updateFilter(colId, e.target.value)}
              placeholder="type to filter…"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 11,
                width: Math.max(90, (value?.length || 10) * 7),
                maxWidth: 180,
                padding: '0 6px',
                color: 'var(--mds-color-neutral-strongest-text, #111)',
              }}
            />
            <button
              onClick={() => removeFilter(colId)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '0 8px 0 2px',
                color: 'var(--mds-color-neutral-weaker-text, #9ca3af)',
                height: '100%',
                borderRadius: '0 20px 20px 0',
                transition: 'color 0.1s ease',
              }}
              title={`Remove ${label} filter`}
              onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--mds-color-error-main-background, #d32f2f)')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--mds-color-neutral-weaker-text, #9ca3af)')}
            >
              <McIcon icon="times" style={{ fontSize: 12 }} />
            </button>
          </div>
        );
      })}

      {/* Add Filter button + popover */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={anchorRef}
          onClick={() => setPickerOpen((v) => !v)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: '1px dashed var(--mds-color-neutral-weak-decoration, #d1d5db)',
            borderRadius: 20,
            background: 'transparent',
            padding: '4px 12px',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--mds-color-neutral-strong-text, #374151)',
            cursor: 'pointer',
            height: 30,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--mds-color-primary-main-background, #0077c5)';
            e.currentTarget.style.color = 'var(--mds-color-primary-main-background, #0077c5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--mds-color-neutral-weak-decoration, #d1d5db)';
            e.currentTarget.style.color = 'var(--mds-color-neutral-strong-text, #374151)';
          }}
        >
          <McIcon icon="plus" style={{ fontSize: 12 }} />
          Add Filter
        </button>

        {pickerOpen && (
          <div
            ref={popoverRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              marginTop: 4,
              width: 240,
              maxHeight: 280,
              background: 'var(--mds-color-neutral-white, #fff)',
              border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Search input */}
            <div style={{ padding: '8px 10px 4px', borderBottom: '1px solid var(--mds-color-neutral-weakest-decoration, #f0f0f0)' }}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search columns…"
                autoFocus
                style={{
                  width: '100%',
                  border: '1px solid var(--mds-color-neutral-weak-decoration, #d1d5db)',
                  borderRadius: 6,
                  padding: '6px 8px',
                  fontSize: 12,
                  outline: 'none',
                  background: 'var(--mds-color-neutral-weakest-background, #f9fafb)',
                }}
              />
            </div>
            {/* Column list */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
              {filteredAvailable.length === 0 ? (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--mds-color-neutral-weaker-text, #9ca3af)', textAlign: 'center' }}>
                  {availableColumns.length === 0 ? 'All columns have filters' : 'No matching columns'}
                </div>
              ) : (
                filteredAvailable.map((col) => (
                  <button
                    key={col.id}
                    onClick={() => addFilter(col.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '7px 14px',
                      fontSize: 12,
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--mds-color-neutral-strong-text, #374151)',
                      transition: 'background 0.1s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--mds-color-neutral-weakest-background, #f3f4f6)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    {col.label || col.id}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Clear all */}
      {activeFilters.length > 0 && (
        <button
          onClick={clearAll}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            border: 'none',
            background: 'transparent',
            padding: '4px 8px',
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--mds-color-error-main-background, #d32f2f)',
            cursor: 'pointer',
            borderRadius: 6,
            transition: 'background 0.1s ease',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--mds-color-error-weakest-background, #fef2f2)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
        >
          <McIcon icon="times" style={{ fontSize: 12 }} />
          Clear all
        </button>
      )}
    </div>
  );
};
